import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AlertBanner from "./AlertBanner";
import PrimaryButton from "../buttons/primaryButton";
import { call as rpc } from "../../../common/jsonRpcRoutes";

interface RewardsSignUpSectionProps {
  /** Booking email — the account must use it for Collins to attach the points. */
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  /** Newsletter preference captured earlier in the journey. */
  readonly newsletterSignup?: boolean;
  /** Called with the new balance (when known) once the account is created. */
  readonly onSignUpSuccess: (pointsBalance?: number) => void;
}

/**
 * Inline "create an account to claim your points" form.
 *
 * Collins attaches reward points to a booking by email —
 * `BookingRewardTransaction::getUserForTransaction()` resolves the user with
 * `new User($booking->email)`, and `UserManager::createAndActivateUser()`
 * replays every earlier booking for that address. Creating the account with the
 * booking's own email is therefore all that is needed; there is no separate
 * booking-to-account linking step.
 *
 * Shared by the /book confirmation and the /confirm page so a guest who paid a
 * deposit (and so returned through the payment redirect) gets the same chance
 * to claim their points.
 */
export default function RewardsSignUpSection({
  email,
  firstName,
  lastName,
  newsletterSignup,
  onSignUpSuccess,
}: RewardsSignUpSectionProps): React.ReactElement {
  const { t } = useTranslation();
  const passwordRef = useRef("");
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [signUpError, setSignUpError] = useState<string | null>(null);

  const handleSignUp = useCallback(async () => {
    const password = passwordRef.current;
    if (!password) return;
    setSignUpLoading(true);
    setSignUpError(null);
    try {
      const result = await rpc("/signUpUser", {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        source: "Booking",
        newsletter_signup: newsletterSignup ?? false,
      });
      if (result.success) {
        onSignUpSuccess(result.user?.points_balance);
      } else {
        setSignUpError(result.error ?? t("sign_up_error", "Sign up failed. Please try again."));
      }
    } catch {
      setSignUpError(t("sign_up_error", "Sign up failed. Please try again."));
    } finally {
      setSignUpLoading(false);
    }
  }, [email, firstName, lastName, newsletterSignup, onSignUpSuccess, t]);

  return (
    <section
      id="gc-confirmation-sign-up"
      aria-label={t("sign_up_section", "Create an account")}
      className="gcw:rounded-md gcw:border gcw:border-gray-200 gcw:bg-white gcw:p-bs-3 gcw:mb-bs-3"
    >
      <p className="gcw:text-body gcw:text-dark-blue gcw:font-semibold">
        {t("sign_up_prompt", "Create an account to earn reward points!")}
      </p>
      <div className="gcw:w-full gcw:mt-4">
        <label
          htmlFor="sign-up-password"
          className="gcw:font-semibold gcw:text-body gcw:text-text-primary gcw:mb-2 gcw:block"
        >
          {t("sign_up_password", "Create a password")}
        </label>
        <input
          id="sign-up-password"
          type="password"
          placeholder={t("sign_up_password", "Create a password")}
          className="container-inputs gcw:w-full gcw:md:w-1/2"
          onChange={(e) => {
            passwordRef.current = e.target.value;
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !signUpLoading) {
              e.preventDefault();
              void handleSignUp();
            }
          }}
          aria-describedby={signUpError ? "sign-up-error" : undefined}
        />
      </div>
      {signUpError && (
        <div id="sign-up-error" className="gcw:mt-2">
          <AlertBanner variant="danger">
            <span role="alert">{signUpError}</span>
          </AlertBanner>
        </div>
      )}
      <div className="gcw:mt-4">
        <PrimaryButton
          text={t("sign_up", "Sign Up")}
          type="button"
          onClick={() => void handleSignUp()}
          disabled={signUpLoading}
          loading={signUpLoading}
          className="gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-pill-lg gcw:bg-dark-purple gcw:text-white gcw:border gcw:border-dark-purple gcw:px-bs-3 gcw:py-3 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:hover:cursor-pointer"
        />
      </div>
    </section>
  );
}
